import { ReactElement, ReactNode, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { AuxScatterSeries } from './auxSensorChannels';
import {
  AUX_COLORS,
  AuxPlotSeriesSpec,
  auxBaseOptions,
  lineSpec,
  scatterSpec,
  styledApexSeries,
} from './auxPlotStyle';
import AuxFitControls, { AuxFitReadout } from './AuxFitControls';
import AuxPlotCard from './AuxPlotCard';
import { useScatterFit } from './useScatterFit';

/** Opting a caller into the least-squares fit machinery. */
export interface AuxFitOptions {
  sliderLabel: string;
  sliderStep?: number;
  /** Offer the held-intercept option alongside the free fit. */
  manualOffset?: boolean;
}

interface AuxComparisonPlotProps {
  title: string;
  /** Stable id for persisting this instance's fit settings. Must be unique. */
  storageKey: string;
  xLabel: string;
  yLabel: string;
  unit: string;
  series: AuxScatterSeries[];
  axisMax: number;
  isLoading?: boolean;
  /**
   * Presence enables the "Show fit" controls — slope/offset/R² readout, fit line
   * and the min/max fit window. Absent, this is a plain agreement scatter.
   */
  fit?: AuxFitOptions;
  /** Small grey caption under the title, e.g. the applied aux time offset. */
  note?: ReactNode;
}

const CHART_HEIGHT = 380;

/**
 * Sensor-agreement scatter: each series is plotted against sensor 1 on the
 * x-axis. A dashed 1:1 reference line is overlaid (via a mixed scatter+line
 * chart) so perfect agreement reads as points lying on the diagonal.
 *
 * With `fit`, a checkbox reveals a least-squares fit of each series: the slope /
 * offset / R² are printed above the chart and the fit line is drawn over the
 * points. Samples outside the fit window are drawn in grey rather than hidden.
 */
export default function AuxComparisonPlot({
  title,
  storageKey,
  xLabel,
  yLabel,
  unit,
  series,
  axisMax,
  isLoading = false,
  fit,
  note,
}: AuxComparisonPlotProps): ReactElement {
  const points = useMemo(() => series.map((s) => s.points), [series]);
  const scatterFit = useScatterFit(points, axisMax, storageKey, !!fit?.manualOffset);
  const { results } = scatterFit;

  const specs = useMemo<AuxPlotSeriesSpec[]>(() => {
    const out: AuxPlotSeriesSpec[] = [];
    results.forEach((r, i) => {
      const color = AUX_COLORS.series[i % AUX_COLORS.series.length];
      out.push(scatterSpec(series[i].name, r.included, color));
      if (r.excluded.length) {
        out.push(scatterSpec(`${series[i].name} (excluded)`, r.excluded, AUX_COLORS.outsideFit));
      }
    });
    results.forEach((r, i) => {
      if (!r.fitLine) return;
      out.push(lineSpec(results.length > 1 ? `${series[i].name} fit` : 'Fit', r.fitLine, AUX_COLORS.fit));
    });
    out.push(lineSpec('1:1', [[0, 0], [axisMax, axisMax]], AUX_COLORS.reference, 6));
    return out;
  }, [results, series, axisMax]);

  const { series: chartSeries, options: styleOptions } = useMemo(() => styledApexSeries(specs), [specs]);

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    return {
      ...base,
      ...styleOptions,
      chart: { ...base.chart, type: 'line' },
      xaxis: {
        type: 'numeric',
        min: 0,
        max: axisMax || undefined,
        tickAmount: 8,
        title: { text: xLabel, style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: string) => `${parseFloat(value).toFixed(0)}` },
      },
      yaxis: {
        min: 0,
        max: axisMax || undefined,
        title: { text: yLabel, style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: number) => (value != null ? value.toFixed(0) : '') },
      },
      tooltip: {
        shared: false,
        intersect: true,
        x: { formatter: (value: number) => `${value.toFixed(1)} ${unit}` },
        y: { formatter: (value: number) => (value != null ? `${value.toFixed(1)} ${unit}` : '') },
      },
    };
  }, [styleOptions, axisMax, xLabel, yLabel, unit]);

  const readouts = useMemo<AuxFitReadout[]>(
    () => results.map((r, i) => ({
      name: series[i].name,
      color: AUX_COLORS.series[i % AUX_COLORS.series.length],
      fit: r.fit,
    })),
    [results, series],
  );

  return (
    <AuxPlotCard
      title={title}
      note={note}
      isLoading={isLoading}
      loadingLabel="Loading comparison…"
      isEmpty={series.length === 0}
      emptyMessage="No comparison data available for this run."
      controls={fit && !isLoading && series.length > 0 ? (
        <AuxFitControls
          showFit={scatterFit.showFit}
          onToggleFit={scatterFit.setShowFit}
          window={scatterFit.window}
          onWindowChange={scatterFit.setWindow}
          sliderLabel={fit.sliderLabel}
          sliderUnit={unit}
          sliderMax={axisMax}
          sliderStep={fit.sliderStep}
          readouts={readouts}
          manualOffset={fit.manualOffset ? {
            enabled: scatterFit.manualOffsetOn,
            value: scatterFit.manualOffset,
            onToggle: scatterFit.setManualOffsetOn,
            onChange: scatterFit.setManualOffset,
            step: fit.sliderStep,
          } : undefined}
          format={{ offsetUnit: unit }}
        />
      ) : null}
    >
      <ReactApexChart options={options} series={chartSeries} type="line" height={CHART_HEIGHT} />
    </AuxPlotCard>
  );
}
